"use client";

import React, {
  type JSX,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  ActivitySquareIcon,
  AlertTriangle,
  Headphones,
  Home,
  HospitalIcon,
  Building2,
  Users,
  User,
  LogOut,
  Calendar,
  ChevronRight,
  ShieldCheck,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { UserAvatarProfile } from "@/components/user-avatar-profile";
import { logOut } from "@/state/reducers/auth.slice";
import { signOut } from "aws-amplify/auth";
import useAuth from "@/hooks/useAuth.ts";
import useDispatchAction from "@/hooks/useDispatchAction.ts";
import { ConnectCCPWidget } from "@/components/ConnectCCPWidget";

type UserRole =
  | "Admin" | "Doctor" | "Nurse" | "Staff" | "Technician"
  | "clinical_staff" | "Clinical_reviewer" | "Patient"
  | "email_Manager" | "SMS_Manager" | "Nutritionist" | "BranchAdmin" | "Branch Admin"
  | "Nurse Practitioner" | "Physician Assistant" | "Clinical Technician" | "Data Coordinator" | "Coordinator" | "Receptionist" | "CareCoordinator";

interface MenuItem {
  title: string;
  icon: React.ElementType;
  url: string;
  roles?: UserRole[];
  requiredClinical?: boolean;
}

const ShimmerMenuItem = () => (
  <div className="flex items-center gap-3 px-3 py-2.5 mx-2 rounded-xl">
    <div className="h-4 w-4 rounded-md bg-[#2d5a3d] animate-pulse flex-shrink-0" />
    <div className="h-3 rounded-md bg-[#2d5a3d] animate-pulse flex-1 group-data-[collapsible=icon]:hidden" />
  </div>
);

const ShimmerUserProfile = () => (
  <div className="flex items-center gap-3 px-3 py-3 mx-2 rounded-xl">
    <div className="h-8 w-8 rounded-full bg-[#2d5a3d] animate-pulse flex-shrink-0" />
    <div className="flex-1 space-y-1.5 group-data-[collapsible=icon]:hidden">
      <div className="h-2.5 rounded-md bg-[#2d5a3d] animate-pulse w-24" />
      <div className="h-2 rounded-md bg-[#2d5a3d] animate-pulse w-32" />
    </div>
  </div>
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="px-5 pt-5 pb-1 group-data-[collapsible=icon]:hidden">
    <span className="text-[10px] font-semibold tracking-widest uppercase text-[#4d9968] select-none">
      {children}
    </span>
  </div>
);

export default function AppSidebar(): JSX.Element {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const dispatch = useDispatchAction();
  const [, forceUpdate] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [criticalAlertCount, setCriticalAlertCount] = useState<number>(0);
  const [isVisible, setIsVisible] = useState(true);
  const [minLoadingTime, setMinLoadingTime] = useState(true);
  const lastRefreshRef = useRef<number>(0);
  const lastNonNullRoleRef = useRef<string | null>(null);
  const REFRESH_COOLDOWN_MS = 5 * 60 * 1000;

  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) { setIsVisible(false); return; }
    setIsVisible(true);
    const now = Date.now();
    const roleMissing = !user?.role && !lastNonNullRoleRef.current;
    if (roleMissing && now - (lastRefreshRef.current || 0) > REFRESH_COOLDOWN_MS) {
      setIsLoading(true); setMinLoadingTime(true);
      lastRefreshRef.current = now; forceUpdate({});
    }
  }, [user?.role]);

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    const handleFocus = () => {
      setIsVisible(true);
      const now = Date.now();
      const roleMissing = !user?.role && !lastNonNullRoleRef.current;
      if (roleMissing && now - (lastRefreshRef.current || 0) > REFRESH_COOLDOWN_MS) {
        setIsLoading(true); setMinLoadingTime(true);
        lastRefreshRef.current = now; forceUpdate({});
      }
    };
    const handleBlur = () => setIsVisible(false);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, [handleVisibilityChange]);

  useEffect(() => {
    if (user?.role) lastNonNullRoleRef.current = user.role;
  }, [user?.role]);

  useEffect(() => {
    if (authLoading) { setIsLoading(true); }
    else if (user?.id && (user?.role || lastNonNullRoleRef.current)) {
      if (!minLoadingTime) setIsLoading(false);
    } else if (user?.id && !user?.role && minLoadingTime) { setIsLoading(true); }
    else { setIsLoading(false); }
    forceUpdate({});
  }, [user?.name, user?.email, user?.role, user?.id, authLoading, isVisible, minLoadingTime]);

  useEffect(() => {
    if (minLoadingTime) {
      const t = setTimeout(() => setMinLoadingTime(false), 800);
      return () => clearTimeout(t);
    }
  }, [minLoadingTime]);

  useEffect(() => {
    let t: NodeJS.Timeout;
    if (isLoading && authLoading && isVisible) {
      t = setTimeout(() => { setIsLoading(false); setMinLoadingTime(false); }, 5000);
    }
    return () => { if (t) clearTimeout(t); };
  }, [isLoading, authLoading, isVisible]);

  useEffect(() => {
    if (!user?.id) return;
    import("@/lib/auth").then(({ default: AuthService }) => {
      AuthService.getCmsAlertStats({ days: 30 }).then((res: any) => {
        if (res?.active_critical) setCriticalAlertCount(res.active_critical);
      }).catch(() => { });
    });
  }, [user?.id]);

  const { open, setOpen } = useSidebar();

  useEffect(() => {
    const saved = localStorage.getItem("sidebar:state");
    if (saved !== null) setOpen(saved === "true");
  }, []);

  useEffect(() => {
    localStorage.setItem("sidebar:state", String(open));
  }, [open]);

  const menu: MenuItem[] = [
    { title: "Dashboard", icon: Home, url: "/dashboard" },
    { title: "Patients", icon: Users, url: "/patients/search", roles: ["Admin", "BranchAdmin", "Branch Admin", "Staff", "Technician", "Doctor", "Nurse", "Nurse Practitioner", "Physician Assistant", "Clinical Technician", "Clinical_reviewer", "CareCoordinator"] },
    { title: "Patient Registration", icon: HospitalIcon, url: "/patients/create", roles: ["Staff", "BranchAdmin", "Branch Admin", "Technician"] },
    { title: "Recorded Responses", icon: ActivitySquareIcon, url: "/questionnaire-responses", roles: ["BranchAdmin", "Branch Admin", "Staff", "Technician", "Doctor", "Nurse", "Nurse Practitioner", "Physician Assistant", "Clinical Technician", "Clinical_reviewer", "CareCoordinator"] },
    { title: "CMS Alerts", icon: AlertTriangle, url: "/cms-alerts", roles: ["Admin", "BranchAdmin", "Branch Admin", "Doctor", "Nurse", "Nurse Practitioner", "Physician Assistant", "Clinical Technician", "clinical_staff", "Clinical_reviewer", "Coordinator", "Technician", "CareCoordinator"] },
    { title: "Audio Archives", icon: Headphones, url: "/admin/audio-archives", roles: ["Admin", "BranchAdmin", "Branch Admin", "Doctor", "Nurse", "Nurse Practitioner", "Physician Assistant", "Clinical Technician", "Clinical_reviewer", "Technician", "Staff", "Coordinator", "Receptionist", "CareCoordinator"] },
    { title: "Staff Users", icon: ShieldCheck, url: "/admin/staff", roles: ["Admin", "BranchAdmin", "Branch Admin"] },
    { title: "Hospital Management", icon: Building2, url: "/admin/hospitals", roles: ["Admin"] },
    { title: "Branch Management", icon: Building2, url: "/admin/hospitals", roles: ["BranchAdmin", "Branch Admin"] },
    { title: "Appointments", icon: Calendar, url: "/appointments", roles: ["Admin", "BranchAdmin", "Branch Admin", "Staff", "Technician", "Doctor", "Nurse", "Nurse Practitioner", "Physician Assistant", "Clinical Technician", "Clinical_reviewer", "CareCoordinator"] },
  ];

  const effectiveRole = user?.role || lastNonNullRoleRef.current || null;

  const filteredMenu = menu.filter((item) => {
    if (!user?.id) return false;

    // Check clinical requirement if set
    if (item.requiredClinical) {
      return !!user.isClinical;
    }

    // Check specific roles if set
    if (item.roles) {
      if (!effectiveRole) return false;

      const normalizedEffectiveRole = effectiveRole.toLowerCase().replace(/\s/g, "");
      const isAllowed = item.roles.some(r => r.toLowerCase().replace(/\s/g, "") === normalizedEffectiveRole);

      // Strict override for Hospital Management: Only Admin role can see it
      if (item.title === "Hospital Management") {
        const isAdmin = normalizedEffectiveRole === "admin";
        if (!isAdmin) {
          console.log(`🛡️ AppSidebar: Hiding Hospital Management from role "${effectiveRole}"`);
          return false;
        }
      }

      return isAllowed;
    }

    // No restrictions
    return true;
  });

  const handleSignOut = async (): Promise<void> => {
    try {
      await signOut();
      dispatch(logOut());
      localStorage.clear(); sessionStorage.clear();
      setTimeout(() => { window.location.href = "/login"; }, 100);
    } catch {
      dispatch(logOut());
      localStorage.clear(); sessionStorage.clear();
      window.location.href = "/login";
    }
  };

  const currentPath = window.location.pathname;

  return (
    <Sidebar
      collapsible="icon"
      className="group border-r-0 bg-[#0f3d24]"
      style={{ backgroundColor: "#0f3d24" }}
    >
      <style>{`
        [data-sidebar="sidebar"] {
          background-color: #0f3d24 !important;
          border-right: none !important;
        }
        .nav-item { color: #a3c9b4; transition: all 0.15s ease; }
        .nav-item svg { color: #4d9968; transition: color 0.15s ease; }
        .nav-item:not(.nav-item-active):hover {
          background-color: rgba(255,255,255,0.07) !important;
          color: #ffffff !important;
        }
        .nav-item:not(.nav-item-active):hover svg { color: #ffffff !important; }
        .nav-item-active {
          background-color: #1a7a4a !important;
          color: #ffffff !important;
          box-shadow: 0 2px 8px rgba(26,122,74,0.4);
        }
        .nav-item-active svg { color: #ffffff !important; }
        [data-sidebar="footer"] [role="menuitem"]:hover {
          background-color: #e8f5ee !important;
          color: #1a7a4a !important;
        }
      `}</style>

      {/* Logo — white sidebar header */}
      <SidebarHeader className="p-0 border-b border-gray-100 bg-white">
        <div className="relative h-[53px] w-full flex items-center justify-center px-3">
          {/* Expanded state — full logo, centered, bigger */}
          <div className="flex items-center justify-center w-full transition-all duration-200 group-data-[collapsible=icon]:opacity-0 group-data-[collapsible=icon]:scale-0 group-data-[collapsible=icon]:absolute">
            <img
              src="/MHAI_FULL_LOGO.png"
              alt="Monitor Health AI"
              style={{ height: "58px", width: "auto", objectFit: "contain" }}
            />
          </div>
          {/* Collapsed state — icon-only logo */}
          <div className="opacity-0 scale-0 transition-all duration-200 group-data-[collapsible=icon]:opacity-100 group-data-[collapsible=icon]:scale-100 absolute left-0 right-0 flex justify-center">
            <div className="h-9 w-9 rounded-xl overflow-hidden flex items-center justify-center">
              <img
                src="/MHAI_LOGO.png"
                alt="MHAI"
                style={{ height: "36px", width: "36px", objectFit: "contain" }}
              />
            </div>
          </div>
        </div>
      </SidebarHeader>

      {/* Nav */}
      <SidebarContent className="px-0 py-2">
        <SidebarMenu>
          {isLoading ? (
            <>
              <SectionLabel>Menu</SectionLabel>
              <ShimmerMenuItem />
              <ShimmerMenuItem />
              <ShimmerMenuItem />
              <ShimmerMenuItem />
            </>
          ) : (
            <>
              <SectionLabel>Menu</SectionLabel>
              {filteredMenu.map((item) => {
                const isActive = currentPath === item.url || currentPath.startsWith(item.url + "/");
                return (
                  <SidebarMenuItem key={item.title} className="px-2 py-0.5">
                    <SidebarMenuButton
                      asChild
                      tooltip={item.title}
                      className={`nav-item rounded-xl px-3 py-2.5 font-medium text-sm ${isActive ? "nav-item-active" : ""}`}
                    >
                      <Link to={item.url} className="flex items-center gap-3">
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
                        {item.title === "CMS Alerts" && criticalAlertCount > 0 && (
                          <span className="ml-auto group-data-[collapsible=icon]:hidden inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white font-bold" style={{ fontSize: "10px" }}>
                            {criticalAlertCount > 99 ? "99+" : criticalAlertCount}
                          </span>
                        )}
                        {item.title !== "CMS Alerts" && isActive && (
                          <ChevronRight className="ml-auto h-3 w-3 opacity-60 group-data-[collapsible=icon]:hidden" />
                        )}
                        {item.title === "CMS Alerts" && isActive && criticalAlertCount === 0 && (
                          <ChevronRight className="ml-auto h-3 w-3 opacity-60 group-data-[collapsible=icon]:hidden" />
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </>
          )}
        </SidebarMenu>
      </SidebarContent>

      {/* Amazon Connect CCP Widget — clinical staff, Admin, and Branch Admin */}
      {(user?.isClinical ||
        ["admin", "branchadmin", "branch admin"].includes(
          (effectiveRole || "").toLowerCase().replace(/\s/g, " ").trim()
        )
      ) && (
          <ConnectCCPWidget
            sidebarExpanded={open}
            userEmail={user?.email || ""}
          />
        )}

      {/* Footer */}
      <SidebarFooter className="border-t border-[#1a5c38] p-0">
        <SidebarMenu>
          {isLoading ? (
            <ShimmerUserProfile />
          ) : (
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30">
                    <div className="h-8 w-8 rounded-full bg-[#1a7a4a] ring-2 ring-[#4d9968] flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <UserAvatarProfile className="h-8 w-8 rounded-full" showInfo={false} useAuthState={true} />
                    </div>
                    <div className="flex-1 min-w-0 text-left group-data-[collapsible=icon]:hidden">
                      <p className="text-sm font-medium text-white truncate leading-tight">{user?.name ?? "User"}</p>
                      <p className="text-xs text-[#6baa88] truncate leading-tight mt-0.5">{user?.email ?? ""}</p>
                    </div>
                    <ChevronRight className="h-3.5 w-3.5 text-[#4d9968] flex-shrink-0 group-data-[collapsible=icon]:hidden" />
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent side="top" align="end" className="min-w-56 rounded-xl shadow-xl border-gray-100 mb-1">
                  <DropdownMenuLabel className="p-0 font-normal">
                    <div className="flex items-center gap-3 px-3 py-2.5">
                      <div className="h-9 w-9 rounded-full bg-[#e8f5ee] flex items-center justify-center flex-shrink-0 overflow-hidden">
                        <UserAvatarProfile className="h-9 w-9 rounded-full" showInfo={false} useAuthState={true} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{user?.name ?? "User"}</p>
                        <p className="text-xs text-gray-400 truncate">{user?.email ?? ""}</p>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")} className="rounded-lg cursor-pointer gap-2.5 py-2">
                    <User className="h-4 w-4 text-[#1a7a4a]" />
                    <span className="text-sm font-medium">Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="rounded-lg cursor-pointer gap-2.5 py-2 text-red-600 focus:text-red-600 focus:bg-red-50">
                    <LogOut className="h-4 w-4" />
                    <span className="text-sm font-medium">Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarFooter>

      {/* Collapse/expand toggle button */}
      <button
        onClick={() => setOpen(!open)}
        aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
        className="absolute top-1/2 -translate-y-1/2 -right-3 z-50 h-6 w-6 rounded-full bg-[#1a7a4a] border-2 border-white flex items-center justify-center opacity-0 group-hover:opacity-100 shadow-md hover:bg-[#22a05e] hover:scale-110 focus:outline-none focus:opacity-100 transition-all duration-200"
      >
        {open ? (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M6.5 2L3.5 5L6.5 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M3.5 2L6.5 5L3.5 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

    </Sidebar>
  );
}